import { Controller } from "@hotwired/stimulus";
import {
  getNextDatapointUrl,
  resolveBoundRouteForController,
} from "../../db/sanitationRoutes.js";
import { getSanitationForm } from "../../db/sanitationForms.js";
import { checkOnlineStatus } from "../../onlineStatus.js";
import {
  navigateToRouteDocument,
  recoverFromBlockedRouteNavigation,
  recoverFromBoundRouteResolutionFailure,
} from "../../routeNavigation.js";

export function openEndedSamplesCheckpointIntent(sanitationRoute) {
  const isLastSection =
    sanitationRoute.current_section_number >=
    sanitationRoute.number_of_tree_sections;
  const hasOnlyOneSection = sanitationRoute.number_of_tree_sections === 1;

  return !isLastSection && !hasOnlyOneSection
    ? "complete_section"
    : "complete_tree";
}

export function applyOpenEndedSamplesCheckpointIntent({
  sanitationRoute,
  sanitationForm,
  completeButton,
  completeButtonText,
  translations,
}) {
  if (
    !sanitationForm.open_ended_samples ||
    !completeButton ||
    !completeButtonText
  ) {
    return null;
  }

  const intent = openEndedSamplesCheckpointIntent(sanitationRoute);
  completeButtonText.textContent =
    intent === "complete_section"
      ? translations.complete_section
      : translations.complete_route_datapoint;
  completeButton.dataset.intent = intent;
  return intent;
}

// Connects to data-controller="sanitation-forms--open-ended-samples-checkpoint"
export default class extends Controller {
  static targets = [
    "continueButton",
    "completeButton",
    "loadingSpinnerContinue",
    "loadingSpinnerComplete",
    "completeButtonText",
    "recoveryMessage",
    "recoveryMessageText",
  ];

  static values = {
    translations: Object,
    routeLauncherPath: String,
    savedOfflineBlockedMessage: String,
    missingActiveRouteMessage: String,
    farmId: Number,
    sanitationFormId: Number,
  };

  static geolocationOptions = {
    enableHighAccuracy: true,
    maximumAge: 40000,
    timeout: 40000,
  };

  async connect() {
    this.disableButtons();
    checkOnlineStatus();
    try {
      const resolution = await resolveBoundRouteForController(
        this,
        "samples_checkpoint",
      );
      if (!resolution.ok) {
        return await this.handleResolutionFailure(resolution);
      }

      const sanitationRoute = resolution.route;
      const sanitationForm = await getSanitationForm(
        sanitationRoute.sanitation_form_id,
      );
      if (!sanitationForm) {
        throw new Error("No se encontró el formulario de sanidad asociado.");
      }

      if (
        sanitationForm.open_ended_samples &&
        this.hasCompleteButtonTarget &&
        this.hasCompleteButtonTextTarget
      ) {
        applyOpenEndedSamplesCheckpointIntent({
          sanitationRoute,
          sanitationForm,
          completeButton: this.completeButtonTarget,
          completeButtonText: this.completeButtonTextTarget,
          translations: this.translationsValue,
        });
      }

      this.setLoadingState("continue", false);
      this.setLoadingState("complete", false);
    } catch (error) {
      alert(`Error al cargar el checkpoint: ${error.message}`);
      this.disableButtons();
    }
  }

  handleResolutionFailure(resolution) {
    return recoverFromBoundRouteResolutionFailure(this, resolution, {
      message: this.missingActiveRouteMessageValue,
      disableControls: () => this.disableControlsForRecovery(),
    });
  }

  disableButtons() {
    if (this.hasContinueButtonTarget) this.continueButtonTarget.disabled = true;
    if (this.hasCompleteButtonTarget) this.completeButtonTarget.disabled = true;
  }

  disableControlsForRecovery() {
    this.setLoadingState("continue", false);
    this.setLoadingState("complete", false);
    this.disableButtons();
  }

  setLoadingState(buttonType, isLoading) {
    let button, spinner;
    if (buttonType === "continue") {
      if (
        !this.hasContinueButtonTarget ||
        !this.hasLoadingSpinnerContinueTarget
      )
        return;
      button = this.continueButtonTarget;
      spinner = this.loadingSpinnerContinueTarget;
    } else {
      if (
        !this.hasCompleteButtonTarget ||
        !this.hasLoadingSpinnerCompleteTarget
      )
        return;
      button = this.completeButtonTarget;
      spinner = this.loadingSpinnerCompleteTarget;
    }
    button.disabled = isLoading;
    spinner.classList.toggle("hidden", !isLoading);
    const buttonTextSpan = button.querySelector("span:not(.loading)");
    if (buttonTextSpan) buttonTextSpan.classList.toggle("invisible", isLoading);
  }

  areButtonsDisabled() {
    const continueDisabled = this.hasContinueButtonTarget
      ? this.continueButtonTarget.disabled
      : true;
    const completeDisabled = this.hasCompleteButtonTarget
      ? this.completeButtonTarget.disabled
      : true;
    return continueDisabled || completeDisabled;
  }

  async continueToNextSample(event) {
    event.preventDefault();
    if (this.areButtonsDisabled()) return;

    this.setLoadingState("continue", true);
    this.setLoadingState("complete", true);

    try {
      const resolution = await resolveBoundRouteForController(
        this,
        "samples_checkpoint",
      );
      if (!resolution.ok) {
        return await this.handleResolutionFailure(resolution);
      }

      const nextUrl = await getNextDatapointUrl(resolution.route.id, {
        intent: "continue_sample",
      });
      await this.navigateAfterLocalStateChange(nextUrl);
    } catch (error) {
      alert(
        `Error al continuar con la siguiente muestra: ${error.message}. Por favor, inténtelo de nuevo.`,
      );
      this.setLoadingState("continue", false);
      this.setLoadingState("complete", false);
    }
  }

  async completeRouteDatapoint(event) {
    event.preventDefault();
    if (this.areButtonsDisabled()) return;

    const buttonIntent = event.currentTarget.dataset.intent || "complete_tree";

    this.setLoadingState("continue", true);
    this.setLoadingState("complete", true);

    try {
      const resolution = await resolveBoundRouteForController(
        this,
        "samples_checkpoint",
      );
      if (!resolution.ok) {
        return await this.handleResolutionFailure(resolution);
      }

      const nextUrl = await getNextDatapointUrl(resolution.route.id, {
        intent: buttonIntent,
      });
      await this.navigateAfterLocalStateChange(nextUrl);
    } catch (error) {
      alert(
        `Error al ${
          buttonIntent === "complete_section"
            ? "finalizar la sección"
            : "finalizar el árbol"
        }: ${error.message}. Por favor, inténtelo de nuevo.`,
      );
      this.setLoadingState("continue", false);
      this.setLoadingState("complete", false);
    }
  }

  async navigateAfterLocalStateChange(nextUrl) {
    const result = await navigateToRouteDocument(nextUrl);
    if (result.allowed) return result;

    await recoverFromBlockedRouteNavigation(this, result, {
      warnMessage:
        "Open-ended samples checkpoint advanced but next screen is missing offline",
      disableControls: () => this.disableControlsForRecovery(),
    });
    return result;
  }
}
